import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { playKitchenAlert, unlockAudio } from '../hooks/useOrderSound';

// ── helpers ──────────────────────────────────────────────────────────────────
const STATION_KEY = 'kds_selected_station';

function elapsed(createdAt) {
  const secs = Math.floor((Date.now() - new Date(createdAt)) / 1000);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

function fmtDuration(secs) {
  if (!secs && secs !== 0) return '—';
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function urgencyColor(createdAt) {
  const mins = (Date.now() - new Date(createdAt)) / 60000;
  if (mins > 20) return '#e74c3c';
  if (mins > 10) return '#f39c12';
  return '#2ecc71';
}

// ── constants ─────────────────────────────────────────────────────────────────
const STATUS = {
  pending:   { label: 'New Order', color: '#3498db', next: 'preparing', nextLabel: '▶ Start Preparing' },
  preparing: { label: 'Preparing', color: '#f39c12', next: 'ready',     nextLabel: '✓ Mark Ready'      },
  ready:     { label: 'Ready',     color: '#2ecc71', next: 'served',    nextLabel: '✓ Served'           },
  served:    { label: 'Served',    color: '#888',    next: null,        nextLabel: null                 },
};
const FILTERS       = ['pending', 'preparing', 'ready', 'served'];
const FILTER_LABELS = { pending: '🔵 New', preparing: '🟡 Cooking', ready: '🟢 Ready', served: '⚫ Served' };
const FILTER_COLORS = { pending: '#3498db', preparing: '#f39c12', ready: '#2ecc71', served: '#666' };

// ── Order Card ────────────────────────────────────────────────────────────────
function OrderCard({ order, onStatusChange, updating }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(p => p + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const st     = STATUS[order.kitchen_status] || STATUS.pending;
  const mins   = (Date.now() - new Date(order.created_at)) / 60000;
  const urgent = urgencyColor(order.created_at);

  // Show how long it's been in current state
  const stateStarted = order.kitchen_status === 'preparing' ? order.preparing_at
                     : order.kitchen_status === 'ready'     ? order.ready_at
                     : order.created_at;
  const stateElapsed = stateStarted ? elapsed(stateStarted) : null;

  return (
    <div style={{
      background: '#111', border: `2px solid ${st.color}44`,
      borderTop: `4px solid ${st.color}`, borderRadius: 10,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      boxShadow: mins > 10 ? `0 0 18px ${urgent}33` : '0 2px 8px #0008',
      transition: 'box-shadow 0.4s',
    }}>
      {/* Header */}
      <div style={{
        background: '#1a1a1a', padding: '10px 14px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid #2a2a2a',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            background: st.color + '22', color: st.color,
            border: `1px solid ${st.color}55`, borderRadius: 5,
            padding: '2px 8px', fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 1,
          }}>{st.label}</span>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#e0d0b0' }}>
            Table {order.table_number}
          </span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: urgent, fontVariantNumeric: 'tabular-nums' }}>
            ⏱ {elapsed(order.created_at)}
          </div>
          {stateElapsed && order.kitchen_status !== 'pending' && (
            <div style={{ fontSize: 10, color: '#555', marginTop: 1 }}>
              {order.kitchen_status === 'preparing' ? 'cooking' : 'waiting'}: {stateElapsed}
            </div>
          )}
        </div>
      </div>

      {/* Meta */}
      <div style={{ padding: '8px 14px 4px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#888' }}>{order.order_type === 'food' ? '🍽️ Food' : '🍺 Bar'}</span>
        {order.waiter_name && <span style={{ fontSize: 11, color: '#666' }}>Waiter: {order.waiter_name}</span>}
        {order.guest_name && order.guest_name !== 'Walk-in' && (
          <span style={{ fontSize: 11, color: '#c8a96e' }}>👤 {order.guest_name}</span>
        )}
        {order.kitchen_station && (
          <span style={{
            fontSize: 10, color: '#e67e22', background: '#e67e2215',
            border: '1px solid #e67e2233', borderRadius: 4, padding: '1px 6px',
          }}>📍 {order.kitchen_station}</span>
        )}
      </div>

      {/* Items */}
      <div style={{ padding: '8px 14px 10px', flex: 1 }}>
        {(order.items || []).map((item, i) => (
          <div key={item.id || i} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
            borderBottom: i < order.items.length - 1 ? '1px solid #1e1e1e' : 'none',
          }}>
            <span style={{
              background: '#c8a96e22', color: '#c8a96e', border: '1px solid #c8a96e44',
              borderRadius: 4, width: 26, height: 26,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 13, flexShrink: 0,
            }}>{item.quantity}</span>
            <span style={{ fontSize: 14, color: '#ddd', fontWeight: 500 }}>{item.name}</span>
          </div>
        ))}
        {order.note && (
          <div style={{
            marginTop: 8, padding: '6px 10px', background: '#1a0e0e',
            border: '1px solid #e74c3c33', borderLeft: '3px solid #e74c3c',
            borderRadius: 4, fontSize: 12, color: '#e0a0a0',
          }}>📝 {order.note}</div>
        )}
      </div>

      {/* Action */}
      {st.next && (
        <button
          onClick={() => onStatusChange(order.id, st.next)}
          disabled={updating === order.id}
          style={{
            width: '100%', padding: '11px',
            background: updating === order.id ? '#222' : st.color,
            color: updating === order.id ? '#555' : '#000',
            border: 'none', borderTop: `1px solid ${st.color}44`,
            fontSize: 13, fontWeight: 800,
            cursor: updating === order.id ? 'not-allowed' : 'pointer',
            letterSpacing: 0.5, fontFamily: 'inherit', transition: 'opacity 0.2s',
          }}
          onMouseOver={e => { if (updating !== order.id) e.currentTarget.style.opacity = '0.85'; }}
          onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
        >
          {updating === order.id ? '…' : st.nextLabel}
        </button>
      )}
    </div>
  );
}

// ── Station Selector Modal ────────────────────────────────────────────────────
function StationModal({ currentStation, knownStations, onSelect, onClose }) {
  const [custom, setCustom] = useState('');

  const builtIn = [
    'Main Kitchen', 'Poolside Kitchen', 'Bar Kitchen',
    'Pastry Section', 'Grill Section', 'Cold Section',
  ];
  const all = Array.from(new Set([...builtIn, ...knownStations])).sort();

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000c', zIndex: 999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#1a1a1a', border: '2px solid #e67e2255',
        borderRadius: 12, padding: 28, width: 360, maxWidth: '95vw',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#e67e22', marginBottom: 6 }}>
          📍 Select Your Kitchen Station
        </div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 20 }}>
          You will only see orders for your selected station.
        </div>

        {/* All stations option */}
        <button
          onClick={() => onSelect('')}
          style={{
            width: '100%', marginBottom: 8, padding: '10px 14px', textAlign: 'left',
            background: currentStation === '' ? '#e67e2222' : '#111',
            border: `1px solid ${currentStation === '' ? '#e67e22' : '#2a2a2a'}`,
            borderRadius: 7, color: currentStation === '' ? '#e67e22' : '#aaa',
            fontWeight: currentStation === '' ? 800 : 400, cursor: 'pointer',
            fontSize: 13, fontFamily: 'inherit',
          }}
        >
          🌐 All Stations {currentStation === '' ? '✓' : ''}
        </button>

        <div style={{ borderTop: '1px solid #2a2a2a', margin: '12px 0' }} />

        {all.map(s => (
          <button key={s} onClick={() => onSelect(s)} style={{
            width: '100%', marginBottom: 6, padding: '10px 14px', textAlign: 'left',
            background: currentStation === s ? '#e67e2222' : '#111',
            border: `1px solid ${currentStation === s ? '#e67e22' : '#2a2a2a'}`,
            borderRadius: 7, color: currentStation === s ? '#e67e22' : '#ccc',
            fontWeight: currentStation === s ? 800 : 400, cursor: 'pointer',
            fontSize: 13, fontFamily: 'inherit',
          }}>
            👨‍🍳 {s} {currentStation === s ? '✓' : ''}
          </button>
        ))}

        <div style={{ borderTop: '1px solid #2a2a2a', margin: '12px 0' }} />

        {/* Custom station input */}
        <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>Or type a custom station:</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={custom}
            onChange={e => setCustom(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && custom.trim()) onSelect(custom.trim()); }}
            placeholder="e.g. Rooftop Kitchen"
            style={{
              flex: 1, background: '#0d0d0d', border: '1px solid #333',
              borderRadius: 6, padding: '8px 10px', color: '#e0d0b0',
              fontSize: 13, fontFamily: 'inherit', outline: 'none',
            }}
          />
          <button
            onClick={() => { if (custom.trim()) onSelect(custom.trim()); }}
            disabled={!custom.trim()}
            style={{
              padding: '8px 14px', background: custom.trim() ? '#e67e22' : '#222',
              color: custom.trim() ? '#000' : '#555', border: 'none',
              borderRadius: 6, fontWeight: 700, cursor: custom.trim() ? 'pointer' : 'not-allowed',
              fontSize: 13, fontFamily: 'inherit',
            }}
          >Set</button>
        </div>
      </div>
    </div>
  );
}

// ── Main KitchenPage ──────────────────────────────────────────────────────────
export default function KitchenPage({ notify }) {
  const { user } = useAuth();

  // Station — default to user's profile station, otherwise last saved, otherwise ''
  const [station, setStation]         = useState(() => {
    const saved = localStorage.getItem(STATION_KEY);
    return saved !== null ? saved : (user?.kitchen_station || '');
  });
  const [showStationModal, setShowStationModal] = useState(false);
  const [knownStations, setKnownStations]       = useState([]);

  const [orders, setOrders]           = useState([]);
  const [filter, setFilter]           = useState('pending');
  const [updating, setUpdating]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const prevPendingRef                = useRef(0);
  const pollRef                       = useRef(null);

  const selectStation = (s) => {
    setStation(s);
    localStorage.setItem(STATION_KEY, s);
    setShowStationModal(false);
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r   = await api.getOrders();
      const all = r.data.results || r.data;
      const kitchenOrders = all.filter(o => !o.is_voided && o.order_type === 'food');

      // Collect all known stations from orders
      const stations = [...new Set(
        kitchenOrders.map(o => o.kitchen_station).filter(Boolean)
      )];
      setKnownStations(stations);

      // Filter by selected station
      const filtered = station
        ? kitchenOrders.filter(o => !o.kitchen_station || o.kitchen_station === station)
        : kitchenOrders;

      const newPending = filtered.filter(o => o.kitchen_status === 'pending').length;
      if (newPending > prevPendingRef.current) playKitchenAlert();
      prevPendingRef.current = newPending;
      setOrders(filtered);
    } catch (_) {
      // silent poll failure
    } finally {
      if (!silent) setLoading(false);
    }
  }, [station]);

  useEffect(() => {
    load(false);
    pollRef.current = setInterval(() => load(true), 8000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const handleStatus = async (orderId, newStatus) => {
    unlockAudio();
    setUpdating(orderId);
    try {
      await api.updateKitchenStatus(orderId, newStatus);
      await load(true);
      const msgs = { preparing: 'Order started', ready: 'Order ready — waiter notified!', served: 'Order served' };
      if (msgs[newStatus]) notify(msgs[newStatus]);
    } catch {
      notify('Failed to update order', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const counts  = FILTERS.reduce((a, s) => ({ ...a, [s]: orders.filter(o => o.kitchen_status === s).length }), {});
  const visible = orders.filter(o => o.kitchen_status === filter);

  return (
    <div onClick={unlockAudio} style={{
      minHeight: '100vh', background: '#0a0a0a', color: '#e0d0b0',
      fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    }}>
      {showStationModal && (
        <StationModal
          currentStation={station}
          knownStations={knownStations}
          onSelect={selectStation}
          onClose={() => setShowStationModal(false)}
        />
      )}

      {/* Top bar */}
      <div style={{
        background: '#111', borderBottom: '2px solid #e67e2244',
        padding: '12px 20px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 22 }}>👨‍🍳</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#e67e22', letterSpacing: 1 }}>
              KITCHEN DISPLAY
            </div>
            <div style={{ fontSize: 11, color: '#666' }}>{user?.name}</div>
          </div>

          {/* Station badge — clickable */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowStationModal(true); }}
            style={{
              background: station ? '#e67e2220' : '#1a1a1a',
              border: `1px solid ${station ? '#e67e22' : '#333'}`,
              borderRadius: 20, padding: '5px 14px',
              color: station ? '#e67e22' : '#555',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.2s',
            }}
          >
            📍 {station || 'All Stations'}
            <span style={{ fontSize: 10, opacity: 0.6 }}>▼</span>
          </button>
        </div>

        {/* Live counts */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {counts.pending > 0 && (
            <div style={{
              background: '#3498db22', border: '1px solid #3498db55',
              borderRadius: 6, padding: '4px 12px', fontSize: 12, color: '#3498db', fontWeight: 700,
              animation: 'kds-pulse 1.5s ease-in-out infinite',
            }}>
              <style>{`@keyframes kds-pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
              🔵 {counts.pending} New
            </div>
          )}
          {counts.preparing > 0 && (
            <div style={{
              background: '#f39c1222', border: '1px solid #f39c1255',
              borderRadius: 6, padding: '4px 12px', fontSize: 12, color: '#f39c12', fontWeight: 700,
            }}>🟡 {counts.preparing} Cooking</div>
          )}
          {counts.ready > 0 && (
            <div style={{
              background: '#2ecc7122', border: '1px solid #2ecc7155',
              borderRadius: 6, padding: '4px 12px', fontSize: 12, color: '#2ecc71', fontWeight: 700,
            }}>🟢 {counts.ready} Ready</div>
          )}
          <div style={{ fontSize: 10, color: '#333', marginLeft: 4 }}>AUTO-REFRESH 8s</div>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{
        background: '#0f0f0f', borderBottom: '1px solid #1e1e1e',
        padding: '0 20px', display: 'flex',
      }}>
        {FILTERS.map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            background: 'transparent', border: 'none',
            borderBottom: filter === s ? `3px solid ${FILTER_COLORS[s]}` : '3px solid transparent',
            color: filter === s ? FILTER_COLORS[s] : '#555',
            padding: '12px 20px', fontSize: 13,
            fontWeight: filter === s ? 800 : 500,
            cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.2s',
            display: 'flex', alignItems: 'center', gap: 7,
          }}>
            {FILTER_LABELS[s]}
            <span style={{
              background: counts[s] > 0 ? FILTER_COLORS[s] + '33' : '#1a1a1a',
              color: counts[s] > 0 ? FILTER_COLORS[s] : '#444',
              border: `1px solid ${counts[s] > 0 ? FILTER_COLORS[s] + '55' : '#2a2a2a'}`,
              borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700,
            }}>{counts[s]}</span>
          </button>
        ))}
      </div>

      {/* Orders grid */}
      <div style={{ padding: '20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#444', paddingTop: 60, fontSize: 14 }}>
            Loading orders…
          </div>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>
              {filter === 'pending' ? '🕐' : filter === 'preparing' ? '🍳' : filter === 'ready' ? '✅' : '📋'}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#444', marginBottom: 6 }}>
              {filter === 'pending' ? 'No new orders' : `No orders ${filter}`}
            </div>
            <div style={{ fontSize: 12, color: '#333' }}>
              {filter === 'pending' ? 'New orders appear here automatically' : 'Check another tab'}
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16, alignItems: 'start',
          }}>
            {visible.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onStatusChange={handleStatus}
                updating={updating}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}